import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { EmailDeliveryStatus } from '../../components/emails/EmailDeliveryStatus';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Badge } from '../../components/ui/badge';
import {
  sendTestEmail, getEmailLogs,
  getDomainStatus, addDomain, verifyDomain, updateEmailConfig,
} from '../../services/api';
import {
  EnvelopeSimple, CheckCircle, Globe, Warning,
  ArrowClockwise, Copy, ShieldCheck, ArrowSquareOut, XCircle, Info
} from '@phosphor-icons/react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

function DnsRecordRow({ rec }) {
  const statusColor = rec.status === 'verified'
    ? 'text-emerald-600 bg-emerald-50'
    : rec.status === 'not_started'
      ? 'text-amber-600 bg-amber-50'
      : 'text-[#5E6D66] bg-[#F0EFEB]';
  const copyValue = (text) => { navigator.clipboard.writeText(text); toast.success('Copie dans le presse-papier'); };

  return (
    <tr className="border-b border-[#F0EFEB] last:border-0">
      <td className="py-2.5 px-3"><Badge variant="outline" className="text-[10px] font-mono border-[#E8E6E1]">{rec.record || rec.type}</Badge></td>
      <td className="py-2.5 px-3"><span className="text-[10px] font-mono text-[#5E6D66]">{rec.type}</span></td>
      <td className="py-2.5 px-3">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-mono text-[#1A2421] break-all max-w-[180px] truncate">{rec.name}</span>
          <button onClick={() => copyValue(rec.name)} className="text-[#5E6D66] hover:text-[#2D5A4C] flex-shrink-0" data-testid={`copy-name-${rec.record}`}><Copy size={12} /></button>
        </div>
      </td>
      <td className="py-2.5 px-3">
        <div className="flex items-center gap-1.5">
          <span className="text-[10px] font-mono text-[#1A2421] break-all max-w-[200px] truncate" title={rec.value}>{rec.value}</span>
          <button onClick={() => copyValue(rec.value)} className="text-[#5E6D66] hover:text-[#2D5A4C] flex-shrink-0" data-testid={`copy-value-${rec.record}`}><Copy size={12} /></button>
        </div>
      </td>
      <td className="py-2.5 px-3">
        <Badge className={`text-[10px] ${statusColor}`} variant="secondary">
          {rec.status === 'verified' ? 'Verifie' : rec.status === 'not_started' ? 'En attente' : rec.status}
        </Badge>
      </td>
    </tr>
  );
}

export function EmailDomainSettings() {
  const [emailLogs, setEmailLogs] = useState([]);
  const [testingEmail, setTestingEmail] = useState(false);
  const [domainStatus, setDomainStatus] = useState(null);
  const [loadingDomain, setLoadingDomain] = useState(true);
  const [newDomain, setNewDomain] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [newSenderEmail, setNewSenderEmail] = useState('');
  const [updatingSender, setUpdatingSender] = useState(false);

  const fetchDomainStatus = async () => {
    setLoadingDomain(true);
    try {
      const res = await getDomainStatus();
      setDomainStatus(res.data);
      if (res.data.sender_email) setNewSenderEmail(res.data.sender_email);
    } catch {} finally { setLoadingDomain(false); }
  };

  useEffect(() => {
    const fetchLogs = async () => { try { const res = await getEmailLogs({ limit: 5 }); setEmailLogs(res.data.items || []); } catch {} };
    fetchLogs();
    fetchDomainStatus();
  }, []);

  const handleTestEmail = async () => {
    setTestingEmail(true);
    try {
      const res = await sendTestEmail();
      toast.success(res.data.message || 'Email de test envoye');
      const logsRes = await getEmailLogs({ limit: 5 });
      setEmailLogs(logsRes.data.items || []);
    } catch { toast.error('Erreur envoi email test'); } finally { setTestingEmail(false); }
  };

  const handleAddDomain = async () => {
    if (!newDomain.trim() || !newDomain.includes('.')) { toast.error('Veuillez entrer un nom de domaine valide'); return; }
    setAddingDomain(true);
    try {
      const res = await addDomain({ domain: newDomain.trim(), region: 'eu-west-1' });
      toast.success(res.data.message);
      setNewDomain('');
      fetchDomainStatus();
    } catch (err) { toast.error(err.response?.data?.detail || "Erreur lors de l'ajout du domaine"); } finally { setAddingDomain(false); }
  };

  const handleVerifyDomain = async (domainId) => {
    setVerifying(true);
    try { const res = await verifyDomain({ domain_id: domainId }); toast.success(res.data.message); fetchDomainStatus(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur verification'); } finally { setVerifying(false); }
  };

  const handleUpdateSender = async () => {
    if (!newSenderEmail.trim() || !newSenderEmail.includes('@')) { toast.error('Email invalide'); return; }
    setUpdatingSender(true);
    try { const res = await updateEmailConfig({ sender_email: newSenderEmail.trim() }); toast.success(res.data.message); fetchDomainStatus(); }
    catch (err) { toast.error(err.response?.data?.detail || 'Erreur mise a jour'); } finally { setUpdatingSender(false); }
  };

  return (
    <div className="space-y-6">
      {/* Email logs */}
      <Card className="border-[#E8E6E1]">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <EnvelopeSimple size={20} className="text-[#2D5A4C]" />
              Emails transactionnels
            </CardTitle>
            <Button variant="outline" size="sm" onClick={handleTestEmail} disabled={testingEmail} data-testid="test-email-btn">
              {testingEmail ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <EnvelopeSimple size={14} className="mr-1" />}
              Envoyer un test
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <Link to="/emails/journal" className="mb-4 inline-flex text-sm font-semibold text-[#2D5A4C] underline underline-offset-4" data-testid="settings-email-journal-link">Journal des confirmations et kits d’adhésion</Link>
          {emailLogs.length > 0 ? (
            <div className="space-y-2">
              <p className="text-xs text-[#5E6D66] uppercase tracking-wider mb-2">Derniers emails</p>
              {emailLogs.map((log) => (
                <div key={log.id} className="flex min-w-0 flex-wrap items-start justify-between gap-3 p-2 rounded border border-[#E8E6E1]">
                  <div className="flex min-w-0 items-start gap-2">
                    <CheckCircle size={14} className={log.status === 'sent' ? 'text-emerald-600' : 'text-[#5E6D66]'} />
                    <span className="break-words text-sm text-[#1A2421]">{log.subject}</span>
                  </div>
                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    <span className="break-all text-xs text-[#5E6D66]">{Array.isArray(log.to) ? log.to.join(', ') : log.to}</span>
                    <EmailDeliveryStatus status={log.status} event={log.delivery_event} testId={`settings-email-status-${log.id}`} />
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-sm text-[#5E6D66]">Aucun email envoye recemment</p>
          )}
        </CardContent>
      </Card>

      {/* Domain config */}
      <Card className="border-[#E8E6E1]" data-testid="domain-config-section">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Globe size={20} className="text-[#2D5A4C]" />
              Configuration domaine email
            </CardTitle>
            <Button variant="outline" size="sm" onClick={fetchDomainStatus} className="border-[#E8E6E1]" data-testid="refresh-domain-status">
              <ArrowClockwise size={14} className="mr-1" /> Actualiser
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {loadingDomain ? (
            <div className="flex items-center justify-center py-6"><Loader2 className="h-5 w-5 animate-spin text-[#2D5A4C]" /></div>
          ) : domainStatus ? (
            <div className="space-y-5">
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div className="p-3 rounded-lg border border-[#E8E6E1] bg-[#F9F8F5]">
                  <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider">API Resend</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    {domainStatus.resend_active ? (<><CheckCircle size={14} className="text-emerald-600" weight="fill" /><span className="text-sm font-medium text-emerald-700">Active</span></>) : (<><XCircle size={14} className="text-red-500" weight="fill" /><span className="text-sm font-medium text-red-600">Non configuree</span></>)}
                  </div>
                </div>
                <div className="p-3 rounded-lg border border-[#E8E6E1] bg-[#F9F8F5]">
                  <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider">Email expediteur</p>
                  <p className="text-sm font-medium text-[#1A2421] mt-1 truncate" data-testid="current-sender">{domainStatus.sender_email}</p>
                </div>
                <div className="p-3 rounded-lg border border-[#E8E6E1] bg-[#F9F8F5]">
                  <p className="text-[10px] text-[#5E6D66] uppercase tracking-wider">Mode</p>
                  <div className="flex items-center gap-1.5 mt-1">
                    {domainStatus.is_test_sender ? (<><Warning size={14} className="text-amber-500" weight="fill" /><span className="text-sm font-medium text-amber-600">Test (onboarding)</span></>) : (<><ShieldCheck size={14} className="text-emerald-600" weight="fill" /><span className="text-sm font-medium text-emerald-700">Production</span></>)}
                  </div>
                </div>
              </div>

              {domainStatus.is_test_sender && (
                <div className="flex items-start gap-3 p-3.5 rounded-lg bg-amber-50 border border-amber-200" data-testid="test-mode-warning">
                  <Warning size={18} className="text-amber-600 mt-0.5 flex-shrink-0" weight="fill" />
                  <div>
                    <p className="text-sm font-medium text-amber-800">Mode test actif</p>
                    <p className="text-xs text-amber-700 mt-0.5 leading-relaxed">
                      L'email expediteur actuel (<code className="font-mono bg-amber-100 px-1 rounded">onboarding@resend.dev</code>) est limite a l'envoi vers votre propre adresse Resend. Pour envoyer a tous vos contacts, vous devez verifier un domaine personnalise.
                    </p>
                  </div>
                </div>
              )}

              {!domainStatus.api_has_domain_access && domainStatus.resend_active && (
                <div className="flex items-start gap-3 p-3.5 rounded-lg bg-blue-50 border border-blue-200" data-testid="api-restriction-notice">
                  <Info size={18} className="text-blue-600 mt-0.5 flex-shrink-0" weight="fill" />
                  <div>
                    <p className="text-sm font-medium text-blue-800">Cle API restreinte</p>
                    <p className="text-xs text-blue-700 mt-0.5 leading-relaxed">La cle API actuelle est configuree pour l'envoi uniquement. Vous pouvez gerer vos domaines directement depuis le tableau de bord Resend.</p>
                    <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 text-xs font-medium text-blue-700 hover:text-blue-900 mt-2" data-testid="resend-domains-link"><ArrowSquareOut size={12} /> Ouvrir resend.com/domains</a>
                  </div>
                </div>
              )}

              {domainStatus.domains && domainStatus.domains.length > 0 && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-[#1A2421]">Domaines configures</h3>
                  {domainStatus.domains.map((domain) => (
                    <div key={domain.id} className="border border-[#E8E6E1] rounded-lg overflow-hidden" data-testid={`domain-${domain.name}`}>
                      <div className="flex items-center justify-between p-3 bg-[#F9F8F5]">
                        <div className="flex items-center gap-2">
                          <Globe size={16} className="text-[#2D5A4C]" />
                          <span className="text-sm font-medium text-[#1A2421]">{domain.name}</span>
                          <Badge variant="secondary" className={domain.status === 'verified' ? 'bg-emerald-100 text-emerald-700' : domain.status === 'pending' ? 'bg-amber-100 text-amber-700' : 'bg-[#F0EFEB] text-[#5E6D66]'}>
                            {domain.status === 'verified' ? 'Verifie' : domain.status === 'pending' ? 'En attente' : domain.status === 'not_started' ? 'Non demarre' : domain.status}
                          </Badge>
                        </div>
                        {domain.status !== 'verified' && (
                          <Button variant="outline" size="sm" onClick={() => handleVerifyDomain(domain.id)} disabled={verifying} className="border-[#2D5A4C] text-[#2D5A4C]" data-testid={`verify-domain-${domain.name}`}>
                            {verifying ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <ShieldCheck size={14} className="mr-1" />} Verifier
                          </Button>
                        )}
                      </div>
                      {domain.records && domain.records.length > 0 && (
                        <div className="overflow-x-auto">
                          <table className="w-full text-left">
                            <thead><tr className="border-b border-[#E8E6E1]"><th className="py-2 px-3 text-[10px] font-semibold text-[#5E6D66] uppercase">Enregistrement</th><th className="py-2 px-3 text-[10px] font-semibold text-[#5E6D66] uppercase">Type</th><th className="py-2 px-3 text-[10px] font-semibold text-[#5E6D66] uppercase">Nom</th><th className="py-2 px-3 text-[10px] font-semibold text-[#5E6D66] uppercase">Valeur</th><th className="py-2 px-3 text-[10px] font-semibold text-[#5E6D66] uppercase">Statut</th></tr></thead>
                            <tbody>{domain.records.map((rec, i) => (<DnsRecordRow key={i} rec={rec} />))}</tbody>
                          </table>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {domainStatus.api_has_domain_access && (
                <div className="space-y-2">
                  <h3 className="text-sm font-semibold text-[#1A2421]">Ajouter un domaine</h3>
                  <div className="flex gap-2">
                    <Input value={newDomain} onChange={(e) => setNewDomain(e.target.value)} placeholder="exemple.com" className="max-w-xs" data-testid="new-domain-input" />
                    <Button onClick={handleAddDomain} disabled={addingDomain || !newDomain.trim()} className="bg-[#2D5A4C] hover:bg-[#1E4136]" data-testid="add-domain-btn">
                      {addingDomain ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <Globe size={16} className="mr-1" />} Ajouter
                    </Button>
                  </div>
                </div>
              )}

              {!domainStatus.api_has_domain_access && domainStatus.resend_active && (
                <div className="space-y-3">
                  <h3 className="text-sm font-semibold text-[#1A2421]">Guide de verification du domaine</h3>
                  <div className="space-y-2.5">
                    {[
                      { step: 1, text: 'Connectez-vous a resend.com/domains', link: 'https://resend.com/domains' },
                      { step: 2, text: 'Cliquez sur "Add Domain" et entrez votre domaine (ex: objectifscop.com)' },
                      { step: 3, text: 'Copiez les enregistrements DNS affiches (SPF, DKIM, MX)' },
                      { step: 4, text: 'Ajoutez ces enregistrements dans la configuration DNS de votre hebergeur' },
                      { step: 5, text: 'Cliquez sur "Verify" dans Resend et attendez la propagation (max 48h)' },
                      { step: 6, text: "Une fois verifie, mettez a jour l'email expediteur ci-dessous (ex: crm@votredomaine.com)" },
                    ].map(({ step, text, link }) => (
                      <div key={step} className="flex items-start gap-3" data-testid={`guide-step-${step}`}>
                        <span className="w-6 h-6 rounded-full bg-[#2D5A4C] text-white flex items-center justify-center text-xs font-bold flex-shrink-0">{step}</span>
                        <p className="text-xs text-[#5E6D66] leading-relaxed pt-0.5">
                          {text}
                          {link && (<a href={link} target="_blank" rel="noopener noreferrer" className="ml-1 text-[#2D5A4C] hover:underline font-medium inline-flex items-center gap-0.5"><ArrowSquareOut size={10} /></a>)}
                        </p>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              <div className="space-y-2 pt-2 border-t border-[#E8E6E1]">
                <h3 className="text-sm font-semibold text-[#1A2421]">Email expediteur</h3>
                <p className="text-xs text-[#5E6D66]">Mettez a jour l'adresse email utilisee comme expediteur pour tous les emails du CRM.</p>
                <div className="flex gap-2">
                  <Input value={newSenderEmail} onChange={(e) => setNewSenderEmail(e.target.value)} placeholder="crm@votredomaine.com" className="max-w-xs" data-testid="sender-email-input" />
                  <Button onClick={handleUpdateSender} disabled={updatingSender || !newSenderEmail.trim() || newSenderEmail === domainStatus.sender_email} variant="outline" className="border-[#2D5A4C] text-[#2D5A4C]" data-testid="update-sender-btn">
                    {updatingSender ? <Loader2 className="h-4 w-4 animate-spin mr-1" /> : <CheckCircle size={16} className="mr-1" />} Appliquer
                  </Button>
                </div>
              </div>
            </div>
          ) : (
            <p className="text-sm text-[#5E6D66]">Impossible de charger la configuration</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
